import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  MapPin, 
  CheckCircle2, 
  XCircle, 
  Building2, 
  RefreshCw,
  TrendingUp,
  Map,
  Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PostcodeCoverage = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/postcode-coverage`, { headers });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch postcode coverage:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-muted-foreground py-12">
        Failed to load postcode coverage data
      </div>
    );
  }

  const { summary, regions, postcodes } = data;

  // Filter postcodes based on search and region
  const filteredPostcodes = postcodes.filter(p => {
    const matchesSearch = searchTerm === '' || p.postcode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = !selectedRegion || p.postcode.startsWith(selectedRegion.split(' ')[0]);
    return matchesSearch && matchesRegion;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" data-testid="coverage-title">Postcode Coverage</h2>
          <p className="text-muted-foreground">Track your search progress across the UK</p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh} 
          disabled={refreshing}
          data-testid="refresh-coverage-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="total-postcodes-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              Postcodes Searched
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summary.total_postcodes_searched}</div>
            <p className="text-xs text-muted-foreground">Unique postcodes</p>
          </CardContent>
        </Card>

        <Card data-testid="with-results-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              With Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-500">{summary.postcodes_with_results}</div>
            <p className="text-xs text-muted-foreground">{summary.success_rate}% success rate</p>
          </CardContent>
        </Card>

        <Card data-testid="no-results-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <XCircle className="w-4 h-4 text-yellow-500" />
              No Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-500">{summary.postcodes_no_results}</div>
            <p className="text-xs text-muted-foreground">All businesses have websites</p>
          </CardContent>
        </Card>

        <Card data-testid="businesses-found-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-500" />
              Businesses Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-500">{summary.total_businesses_found}</div>
            <p className="text-xs text-muted-foreground">Potential customers</p>
          </CardContent>
        </Card>
      </div>

      {/* Regions Overview */}
      {regions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Map className="w-5 h-5 text-primary" />
              Coverage by Region
            </CardTitle>
            <CardDescription>Click a region to filter postcodes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {selectedRegion && (
                <Badge 
                  variant="default"
                  className="cursor-pointer"
                  onClick={() => setSelectedRegion(null)}
                >
                  Clear filter: {selectedRegion} ×
                </Badge>
              )}
              {regions.slice(0, 20).map((region) => (
                <Badge
                  key={region.name}
                  variant={selectedRegion === region.name ? "default" : "outline"}
                  className={`cursor-pointer hover:bg-primary/20 ${
                    region.with_results > 0 ? 'border-green-500/50' : 'border-yellow-500/50'
                  }`}
                  onClick={() => setSelectedRegion(selectedRegion === region.name ? null : region.name)}
                  data-testid={`region-${region.name}`}
                >
                  {region.name}
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-white/10 text-xs">
                    {region.count}
                  </span>
                  {region.with_results > 0 && (
                    <CheckCircle2 className="w-3 h-3 ml-1 text-green-500" />
                  )}
                </Badge>
              ))}
              {regions.length > 20 && (
                <Badge variant="outline" className="text-muted-foreground">
                  +{regions.length - 20} more
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Postcodes Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Searched Postcodes</CardTitle>
              <CardDescription>
                {filteredPostcodes.length} postcodes {searchTerm && `matching "${searchTerm}"`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search postcodes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-48"
                data-testid="postcode-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPostcodes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No postcodes searched yet</p>
              <p className="text-sm">Start searching from the Find Businesses page</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Postcode</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Businesses</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Searches</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Last Searched</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPostcodes.map((postcode, index) => (
                    <tr 
                      key={postcode.postcode} 
                      className="border-b border-white/5 hover:bg-white/5"
                      data-testid={`postcode-row-${index}`}
                    >
                      <td className="py-3 px-3">
                        <span className="font-mono font-medium">{postcode.postcode}</span>
                      </td>
                      <td className="py-3 px-3">
                        {postcode.has_results ? (
                          <Badge variant="outline" className="border-green-500 text-green-400 gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Has Results
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-yellow-500 text-yellow-400 gap-1">
                            <XCircle className="w-3 h-3" />
                            No Results
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <span className={`font-medium ${postcode.businesses_found > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                          {postcode.businesses_found}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-muted-foreground">{postcode.search_count || 1}×</span>
                      </td>
                      <td className="py-3 px-3 text-sm text-muted-foreground">
                        {new Date(postcode.last_searched).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips Card */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Coverage Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
              Focus on postcodes that show "Has Results" - these have businesses without websites
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-primary mt-0.5" />
              Try searching adjacent postcodes to areas with good results
            </li>
            <li className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-blue-500 mt-0.5" />
              Areas with many restaurants, salons, and local services tend to have more opportunities
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default PostcodeCoverage;
